import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { getAccessToken } from "../awsAuth";
import { useSubscription } from "../contexts/SubscriptionContext";
import { useWallet } from "../contexts/WalletContext";

function niceDate(ts) {
  if (!ts) return null;
  try {
    const d = new Date(Number(ts));
    return d.toLocaleString("uk-UA", { timeZone: "Europe/Kyiv" });
  } catch {
    return null;
  }
}

export default function PaymentReturn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const subscription = useSubscription();
  const wallet = useWallet();
  const paymentId = searchParams.get("paymentId") || "";
  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState("");
  const [polling, setPolling] = useState(false);
  const pollCountRef = useRef(0);
  const maxPoll = 20; // 20 * 3s = 60s

  // PACKAGE #12 Phase C — Boost auto-resume safety net. The webhook-driven
  // fulfillment path already attempts this automatically the instant the
  // top-up is confirmed (see paymentFulfillmentService.js). This page calls
  // the same idempotent endpoint once more purely as a fallback for the
  // case where that attempt never ran (e.g. a server restart between the
  // Coins credit and the resume attempt) — never a condition for the boost
  // to happen, just insurance so the user isn't left stuck.
  const [boostResumeState, setBoostResumeState] = useState("idle"); // idle | resuming | active | failed
  const boostResumeAttempted = useRef(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

  const api = (path) => (API_BASE ? `${API_BASE}${path}` : path);

  async function fetchStatus(id) {
    if (!id) return;
    setLoading(true);
    setError("");
    try {
      const token = await getAccessToken?.().catch(() => null);
      const res = await fetch(api(`/api/payments/${encodeURIComponent(id)}`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status} ${body}`);
      }
      const data = await res.json();
      setPayment(data);
      setLoading(false);
      return data;
    } catch (e) {
      setLoading(false);
      setError("Помилка при отриманні статусу платежу.");
      console.error("fetchStatus error:", e);
      return null;
    }
  }

  useEffect(() => {
    if (!paymentId) {
      setError("Немає paymentId у URL.");
      return;
    }

    fetchStatus(paymentId).then((d) => {
      if (d && d.status && d.status.toLowerCase() === "pending") {
        setPolling(true);
        pollCountRef.current = 0;
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId]);

  useEffect(() => {
    if (!polling) return;
    const t = setInterval(async () => {
      pollCountRef.current += 1;
      const d = await fetchStatus(paymentId);
      if (!d) {
        setPolling(false);
        clearInterval(t);
        return;
      }
      const st = (d.status || "").toLowerCase();
      if (st === "succeeded" || st === "failed" || pollCountRef.current >= maxPoll) {
        setPolling(false);
        clearInterval(t);
      }
    }, 3000);

    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling, paymentId]);

  // The provider webhook can legitimately arrive after this 60s window
  // (network delay, provider-side queueing) — resuming polling on request
  // is cheap and avoids leaving the user stuck on "В обробці…" forever with
  // no way forward besides a manual page refresh.
  const gaveUpWhilePending =
    !polling &&
    !loading &&
    pollCountRef.current >= maxPoll &&
    String(payment?.status || "").toLowerCase() === "pending";

  const onRetryCheck = () => {
    pollCountRef.current = 0;
    setPolling(true);
  };

  const boostIntent = payment?.metadata?.boostIntent || null;
  const isSucceeded = String(payment?.status || "").toLowerCase() === "succeeded";
  const isSubscriptionPurpose = payment?.purpose === "subscription" || payment?.purpose === "premium";
  const isWalletTopupPurpose = payment?.purpose === "wallet_topup";

  // Refetch subscription/wallet state once the backend confirms the
  // payment — the plan/Coins were already applied server-side by the
  // Monobank webhook; this just makes the UI pick up the new state without
  // requiring a manual reload.
  useEffect(() => {
    if (!isSucceeded) return;
    if (isSubscriptionPurpose) subscription.refresh();
    if (isWalletTopupPurpose) wallet.refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSucceeded, isSubscriptionPurpose, isWalletTopupPurpose]);

  // Boost auto-resume safety net (see boostResumeState declaration above).
  useEffect(() => {
    if (!isSucceeded || !boostIntent || boostResumeAttempted.current) return;
    boostResumeAttempted.current = true;

    (async () => {
      setBoostResumeState("resuming");
      try {
        const token = await getAccessToken?.().catch(() => null);
        const res = await fetch(api(`/api/boost/resume-intent/${encodeURIComponent(paymentId)}`), {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));

        if (res.ok && (data?.active || data?.resumed)) {
          setBoostResumeState("active");
          wallet.refresh();
          return;
        }
        setBoostResumeState("failed");
      } catch (e) {
        setBoostResumeState("failed");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSucceeded, boostIntent, paymentId]);

  const onBack = () => navigate(-1);

  return (
    <div style={{ maxWidth: 820, margin: "36px auto", padding: 24 }}>
      <button onClick={onBack} style={{ marginBottom: 20 }}>← Назад</button>

      <h2>Результат платежу</h2>

      {!paymentId && (
        <div style={{ marginTop: 16, color: "#c33" }}>
          Не знайдено paymentId. Якщо ви перейшли з платіжної сторінки — переконайтесь, що у URL є ?paymentId=...
        </div>
      )}

      {error && <div style={{ marginTop: 16, color: "#c33" }}>{error}</div>}

      {loading && <div style={{ marginTop: 16 }}>Завантаження статусу...</div>}

      {payment && (
        <div style={{ marginTop: 18, border: "1px solid #eee", padding: 18, borderRadius: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 14, color: "#666" }}>Payment ID</div>
              <div style={{ fontWeight: 600 }}>{payment.paymentId}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 14, color: "#666" }}>Provider</div>
              <div>{payment.provider || "—"}</div>
            </div>
          </div>

          <hr style={{ margin: "12px 0" }} />

          <div style={{ display: "flex", gap: 18 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: "#666" }}>Сума</div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>{(Number(payment.amount || 0) / 100).toFixed(2)} ₴</div>
              <div style={{ marginTop: 8, fontSize: 13, color: "#666" }}>
                Опис: {payment.description || "—"}
              </div>
            </div>

            <div style={{ width: 220 }}>
              <div style={{ fontSize: 13, color: "#666" }}>Статус</div>
              <div style={{ marginTop: 8, fontWeight: 700, fontSize: 18 }}>
                {String(payment.status || "").toLowerCase() === "succeeded" ? (
                  <span style={{ color: "green" }}>Оплачено</span>
                ) : String(payment.status || "").toLowerCase() === "pending" ? (
                  <span style={{ color: "#b8860b" }}>В обробці…</span>
                ) : (
                  <span style={{ color: "#c33" }}>Неуспішно</span>
                )}
              </div>

              <div style={{ marginTop: 12, fontSize: 12, color: "#666" }}>
                Оновлено: {niceDate(payment.updatedAt)}
              </div>

              {gaveUpWhilePending && (
                <button onClick={onRetryCheck} style={{ marginTop: 10 }}>
                  Перевірити ще раз
                </button>
              )}
            </div>
          </div>

          {isSucceeded && (
            <div style={{ marginTop: 18, padding: 12, borderRadius: 8, background: "#f6fffa", border: "1px solid #e6f6ea" }}>
              {isSubscriptionPurpose && (
                <>
                  <strong>Готово.</strong> Тариф успішно активовано.
                </>
              )}

              {isWalletTopupPurpose && !boostIntent && (
                <>
                  <strong>Готово.</strong> Гаманець поповнено.
                </>
              )}

              {isWalletTopupPurpose && boostIntent && (
                <>
                  {boostResumeState === "active" && (
                    <>
                      <strong>Готово.</strong> Оголошення піднято.
                    </>
                  )}
                  {(boostResumeState === "resuming" || boostResumeState === "idle") && (
                    <>Оплата пройшла, піднімаємо оголошення…</>
                  )}
                  {boostResumeState === "failed" && (
                    <>
                      <strong>Tokens нараховано.</strong> Не вдалося автоматично підняти оголошення —
                      відкрийте розділ "Підняття" і спробуйте ще раз, повторної оплати не знадобиться.
                      {" "}
                      <Link to="/settings/promotion/boost">Перейти до підняття</Link>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {!payment && !loading && paymentId && (
        <div style={{ marginTop: 16 }}>
          Не вдалося отримати інформацію про платіж. Спробуйте оновити сторінку або зверніться до підтримки.
        </div>
      )}
    </div>
  );
}
